import React, { useEffect, useMemo, useState } from 'react';
import { MIcon } from '../../../lib/micon';
import { apiFetch, ApiError, zeigeApiFehler } from '../../../lib/api';
import { toast } from '../../../lib/toast';
import { LdSheet } from '../../../components/ui/LdSheet';
import { LdSheetHeader, LdSheetSaveBar } from '../../../components/ld/LdForm';
import type { MitarbeitWoche } from './profil-api';
import { note, tm, heuteIso } from './format';

type FachWochen = { fach: string; schnitt: number | null; wochen: (MitarbeitWoche & { bis: string; bemerkung: string | null; eintraege: number })[] };
type Vorhanden = { id: number; datum: string; note: number; kontext: string | null };

// Welle P1b: Mitarbeit für eine Schulwoche eintragen (POST /api/schueler/:id/mitarbeit).
// Wochen und Werte kommen aus GET …/mitarbeit — dieselbe Quelle wie die Notengrundlage.
export function MitarbeitWocheSheet({ open, schuelerId, schuelerName, faecher, startFach, startWoche, onClose, onGespeichert }: {
  open: boolean; schuelerId: number; schuelerName: string; faecher: string[];
  startFach?: string; startWoche?: string;
  onClose: () => void; onGespeichert: () => void;
}) {
  const [daten, setDaten] = useState<FachWochen[] | null>(null);
  const [fehler, setFehler] = useState(false);
  const [fach, setFach] = useState('');
  const [woche, setWoche] = useState('');
  const [wert, setWert] = useState<number | null>(null);
  const [tendenz, setTendenz] = useState<'+' | '-' | null>(null);
  const [bemerkung, setBemerkung] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [vorhanden, setVorhanden] = useState<Vorhanden[] | null>(null);
  const [meldung, setMeldung] = useState('');

  useEffect(() => {
    if (!open) return;
    setDaten(null); setFehler(false); setWert(null); setTendenz(null); setBemerkung(''); setVorhanden(null); setMeldung('');
    setFach(startFach || faecher[0] || ''); setWoche(startWoche ?? '');
    apiFetch<{ faecher: FachWochen[] }>(`/api/schueler/${schuelerId}/mitarbeit`)
      .then(d => setDaten(d.faecher)).catch(() => setFehler(true));
  }, [open, schuelerId, startFach, startWoche, faecher]);

  const alleFaecher = useMemo(() => Array.from(new Set([...faecher, ...(daten ?? []).map(f => f.fach)])), [faecher, daten]);
  const heute = heuteIso();
  // die letzten 6 Schulwochen bis heute (Ferien zählen nicht), neueste rechts
  const wochen = useMemo(() => {
    const fw = (daten ?? []).find(f => f.fach === fach) ?? (daten ?? [])[0];
    return (fw?.wochen ?? []).filter(w => !w.ferien && w.von <= heute).slice(-6);
  }, [daten, fach, heute]);
  useEffect(() => {
    if (!wochen.length) return;
    if (!woche || !wochen.some(w => w.von === woche)) setWoche(wochen[wochen.length - 1].von);
  }, [wochen, woche]);
  const gewaehlt = wochen.find(w => w.von === woche);

  async function speichern(ersetzen = false) {
    if (!fach || !woche || wert == null) return;
    setLaeuft(true); setMeldung('');
    try {
      await apiFetch(`/api/schueler/${schuelerId}/mitarbeit`, { method: 'POST', body: JSON.stringify({ fach, woche, wert, tendenz, bemerkung: bemerkung.trim() || null, ersetzen }) });
      toast(`Mitarbeit ${fach} eingetragen`, 'success');
      onGespeichert(); onClose();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setVorhanden(((err.body as { vorhanden?: Vorhanden[] } | null)?.vorhanden ?? []));
      } else if (err instanceof ApiError && err.status === 400) setMeldung(err.message);
      else zeigeApiFehler(err, 'Speichern fehlgeschlagen');
    } finally { setLaeuft(false); }
  }

  return (
    <LdSheet open={open} onClose={onClose} maxWidth={520} ariaLabel="Mitarbeit eintragen">
      <div className="ld-mws">
        <LdSheetHeader icon={<MIcon name="back_hand" size={20} />} title={gewaehlt ? `${fach || 'Mitarbeit'}, KW ${gewaehlt.kw}` : 'Mitarbeit eintragen'}
          subtitle={gewaehlt ? `${schuelerName} · ${tm(gewaehlt.von)}–${tm(addTage(gewaehlt.von, 4))}${gewaehlt.status === 'beobachtet' ? ` · bisher ${note(gewaehlt.wert)}` : ' · noch keine Beobachtung'}` : schuelerName}
          onClose={onClose} />
        {fehler ? <div className="ld-pu-fehler" role="alert"><span>Wochen konnten nicht geladen werden.</span></div> : (
          <>
            {alleFaecher.length > 1 && (
              <div className="ld-mws__gruppe" role="group" aria-label="Fach">
                <div className="ld-mws__label">Fach</div>
                <div className="ld-mws__chips">
                  {alleFaecher.map(f => <button key={f} type="button" aria-pressed={fach === f} className={`ld-mws__chip${fach === f ? ' an' : ''}`} onClick={() => setFach(f)}>{f}</button>)}
                </div>
              </div>
            )}
            <div className="ld-mws__gruppe" role="group" aria-label="Woche">
              <div className="ld-mws__label">Woche</div>
              <div className="ld-mws__wochen">
                {!daten ? <span className="ld-mws__leer">Lädt …</span> : wochen.length === 0 ? <span className="ld-mws__leer">Keine Schulwoche im Halbjahr bisher.</span>
                  : wochen.map(w => (
                    <button key={w.von} type="button" aria-pressed={woche === w.von} className={`ld-mws__woche${woche === w.von ? ' an' : ''}${w.status === 'luecke' ? ' ld-mws__woche--luecke' : ''}`} onClick={() => { setWoche(w.von); setVorhanden(null); }}>
                      <small>KW {w.kw}</small><b>{w.status === 'beobachtet' ? note(w.wert) : w.status === 'entschuldigt' ? 'e' : '+'}</b>
                    </button>
                  ))}
              </div>
            </div>
            <div className="ld-mws__gruppe" role="group" aria-label="Wert">
              <div className="ld-mws__label">Wert</div>
              <div className="ld-mws__werte">
                {[1, 2, 3, 4, 5].map(n => <button key={n} type="button" aria-pressed={wert === n} className={`ld-mws__wert${wert === n ? ' an' : ''}`} onClick={() => setWert(n)}>{n}</button>)}
              </div>
              <div className="ld-mws__tendenz">
                {([['+', 'Tendenz +'], [null, 'ohne'], ['-', 'Tendenz −']] as const).map(([t, l]) => (
                  <button key={l} type="button" aria-pressed={tendenz === t} className={`ld-mws__chip${tendenz === t ? ' an' : ''}`} onClick={() => setTendenz(t)}>{l}</button>
                ))}
              </div>
            </div>
            <textarea className="ld-mws__bemerkung" rows={2} placeholder="Bemerkung (optional)" value={bemerkung} onChange={e => setBemerkung(e.target.value)} />
            {meldung && <div className="ld-mws__meldung" role="alert">{meldung}</div>}
            {vorhanden && (
              <div className="ld-mws__meldung" role="alert">
                Für diese Woche steht schon {vorhanden.length === 1 ? `eine ${vorhanden[0].note}` : `${vorhanden.length} Einträge`}. Ersetzen?
                <button type="button" className="ld-pu-link" onClick={() => speichern(true)} disabled={laeuft}>Ja, ersetzen</button>
              </div>
            )}
            <LdSheetSaveBar onClick={() => speichern(false)} disabled={!fach || !woche || wert == null} loading={laeuft}>Eintragen</LdSheetSaveBar>
          </>
        )}
      </div>
    </LdSheet>
  );
}
function addTage(iso: string, n: number) { const d = new Date(iso + 'T12:00:00Z'); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); }
